/**
 * Phone validation utilities v2 - Multi-country support
 * Uses robust validation compatible with python-phonenumbers backend
 * 
 * This is the NEW implementation - replaces old phoneValidation.ts for new code.
 */

import { parsePhoneNumberFromString } from 'libphonenumber-js';

export interface PhoneValidationResult {
  isValid: boolean;
  formatted: string; // E.164 format: 962771234567
  error?: string;
}

/**
 * Client-side validation for phone numbers
 * 
 * IMPORTANT: This is BASIC validation only for UX purposes (immediate feedback).
 * The backend (python-phonenumbers) does AUTHORITATIVE validation for all countries.
 * 
 * This function only checks:
 * - Not empty
 * - Basic length (4-20 characters)
 * - Valid characters (digits, +, spaces, dashes, parentheses)
 * 
 * Country-specific validation (e.g., Jordan format requirements) is handled by the backend.
 * 
 * @param phone - Phone number to validate
 * @param countryCode - Country dial code (e.g., "+962", "+1") - used for context only, not validation
 * @returns Validation result object
 */
export const validatePhoneClientSide = (
  phone: string,
  countryCode: string
): PhoneValidationResult => {
  if (!phone || typeof phone !== 'string') {
    return {
      isValid: false,
      formatted: '',
      error: 'Phone number is required'
    };
  }

  // Remove all spaces, dashes, and parentheses for basic cleaning
  const cleaned = phone.replace(/[\s\-().]/g, '');
  
  // Check for empty after cleaning
  if (!cleaned) {
    return {
      isValid: false,
      formatted: '',
      error: 'Phone number cannot be empty'
    };
  }

  // Basic length check (phones are usually 7-15 digits local part)
  if (cleaned.length < 4 || cleaned.length > 20) {
    return {
      isValid: false,
      formatted: '',
      error: 'Phone number length invalid'
    };
  }

  // Check for valid characters (digits, +, and country codes)
  if (!/^[\d+-\s().]+$/.test(phone)) {
    return {
      isValid: false,
      formatted: '',
      error: 'Phone number contains invalid characters'
    };
  }

  // Clean digits only
  const digitsOnly = cleaned.replace(/[^\d]/g, '');
  
  if (digitsOnly.length < 4) {
    return {
      isValid: false,
      formatted: '',
      error: 'Phone number too short'
    };
  }

  // Client-side validation passed - backend will do proper validation
  return {
    isValid: true,
    formatted: cleaned,
    error: undefined
  };
};

/**
 * Format phone for API submission
 * Combines country code and phone number
 * 
 * @param phone - Local phone number
 * @param countryCode - Country dial code (e.g., "+962")
 * @returns Formatted phone number string
 */
export const formatPhoneForAPI = (phone: string, countryCode: string): string => {
  // Clean the phone number
  const cleaned = phone.replace(/[\s\-().]/g, '');
  const digitsOnly = cleaned.replace(/[^\d]/g, '');
  
  // Remove leading zero if present
  let localPart = digitsOnly;
  if (localPart.startsWith('0')) {
    localPart = localPart.substring(1);
  }
  
  // Get just the digits from country code
  const countryDigits = countryCode.replace('+', '').replace(/[^\d]/g, '');
  
  // Return E.164 format without + (backend adds it)
  // Format: countryDigits + localPart
  return countryDigits + localPart;
};

/**
 * Extract country code from E.164 phone number
 * 
 * @param e164Phone - Phone in E.164 format (e.g., "962771234567")
 * @returns Country dial code (e.g., "962")
 */
export const extractCountryCodeFromE164 = (e164Phone: string): string => {
  if (!e164Phone) return '';
  
  // Remove any non-digits
  const digits = e164Phone.replace(/[^\d]/g, '');
  
  // Try to extract country code (first 1-3 digits)
  // Common patterns:
  // 1 digit: 1 (US/CA)
  // 2 digits: 33, 39, 40, etc.
  // 3 digits: 962 (Jordan), 966 (Saudi), etc.
  
  if (digits.startsWith('1') && digits.length === 11) {
    return '1'; // US/Canada
  } else if (digits.startsWith('962') && digits.length === 12) {
    return '962'; // Jordan
  } else if (digits.startsWith('966') && digits.length === 12) {
    return '966'; // Saudi Arabia
  } else if (digits.startsWith('971') && digits.length === 12) {
    return '971'; // UAE
  } else if (digits.startsWith('44') && digits.length >= 11) {
    return '44'; // UK
  }
  
  // Default: try first 3 digits
  return digits.substring(0, 3);
};

/**
 * Parse E.164 phone to get local number and country
 * 
 * @param e164Phone - Phone in E.164 format
 * @returns Object with local number and country code
 */
export const parseE164Phone = (e164Phone: string): { localNumber: string; countryCode: string } => {
  if (!e164Phone) {
    return { localNumber: '', countryCode: '' };
  }
  
  // Try to use libphonenumber-js for accurate parsing
  try {
    const parsed = parsePhoneNumberFromString(e164Phone);
    if (parsed && parsed.isValid()) {
      return {
        localNumber: parsed.nationalNumber,
        countryCode: parsed.country || ''
      };
    }
  } catch (e) {
    // Fall through to manual parsing
  }
  
  const digits = e164Phone.replace(/[^\d]/g, '');
  
  // Try to detect country code manually as fallback
  let countryCode = '';
  let localNumber = '';
  
  if (digits.startsWith('1') && digits.length === 11) {
    countryCode = 'US';
    localNumber = digits.substring(1);
  } else if (digits.startsWith('962') && digits.length === 12) {
    countryCode = 'JO';
    localNumber = digits.substring(3);
  } else if (digits.startsWith('966') && digits.length === 12) {
    countryCode = 'SA';
    localNumber = digits.substring(3);
  } else if (digits.startsWith('971') && digits.length === 12) {
    countryCode = 'AE';
    localNumber = digits.substring(3);
  } else if (digits.startsWith('44') && digits.length >= 11) {
    countryCode = 'GB';
    localNumber = digits.substring(2);
  } else if (digits.startsWith('33') && digits.length >= 11) {
    countryCode = 'FR';
    localNumber = digits.substring(2);
  } else if (digits.startsWith('49') && digits.length >= 11) {
    countryCode = 'DE';
    localNumber = digits.substring(2);
  } else if (digits.startsWith('91') && digits.length === 12) {
    countryCode = 'IN';
    localNumber = digits.substring(2);
  } else if (digits.startsWith('86') && digits.length === 13) {
    countryCode = 'CN';
    localNumber = digits.substring(2);
  } else if (digits.startsWith('81') && digits.length >= 11) {
    countryCode = 'JP';
    localNumber = digits.substring(2);
  } else if (digits.startsWith('20') && digits.length >= 11) {
    countryCode = 'EG';
    localNumber = digits.substring(2);
  } else {
    // Default: assume Jordan
    countryCode = 'JO';
    localNumber = digits.startsWith('962') ? digits.substring(3) : digits;
  }
  
  return { localNumber, countryCode };
};

/**
 * Get placeholder text for phone input based on country
 * 
 * @param countryCode - Country dial code (e.g., "+962")
 * @returns Placeholder text
 */
export const getPhonePlaceholderV2 = (countryCode: string): string => {
  switch (countryCode) {
    case '+962':
      return '0771234567';
    case '+1':
      return '2015551234';
    case '+966':
      return '0501234567';
    case '+971':
      return '0501234567';
    case '+44':
      return '7123456789';
    case '+49':
      return '1512345678';
    case '+33':
      return '612345678';
    case '+39':
      return '3123456789';
    case '+34':
      return '612345678';
    default:
      return 'Enter phone number';
  }
};

/**
 * Get help text for phone input based on country
 * 
 * @param countryCode - Country dial code
 * @returns Help text
 */
export const getPhoneHelpTextV2 = (countryCode: string): string => {
  return 'Enter your phone number';
};

